import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";

/*
  RecoveryArc — a scroll-locked, cinematic photographic section that
  narrates the arc from addiction to recovery in four stages. Uses
  crossfading full-bleed photography + Ken Burns + parallax + a subtle
  3D perspective card tilt. No cartoon SVGs — all real photography.
*/

const STAGES = [
  {
    key: "trapped",
    eyebrow: "Stage 01 · Trapped",
    title: "The weight of the storm",
    body: "Isolation, shame, and the pull of addiction feel heavier than the sky above. You are not alone in this — many begin here.",
    quote: "\"I felt like the whole sky was pressing down on me.\"",
    stat: "1 in 5",
    statLabel: "Canadians live with addiction or mental-health issues",
    img: "https://images.unsplash.com/photo-1516912481808-3406841bd33c?auto=format&w=2000&q=75",
    overlay:
      "linear-gradient(180deg, rgba(6,10,18,0.65) 0%, rgba(6,10,18,0.35) 45%, rgba(6,10,18,0.75) 100%)",
    accent: "#CE6A4B",
  },
  {
    key: "reach",
    eyebrow: "Stage 02 · Reach",
    title: "A first crack of light",
    body: "One phone call. One honest sentence. The moment you reach out, the ground shifts — and a path begins to appear.",
    quote: "\"Calling was the hardest and best thing I have ever done.\"",
    stat: "Same-day",
    statLabel: "Admission available for many private-pay & GreenShield clients",
    img: "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&w=2000&q=75",
    overlay:
      "linear-gradient(180deg, rgba(10,20,32,0.55) 0%, rgba(10,20,32,0.25) 50%, rgba(10,20,32,0.65) 100%)",
    accent: "#CE6A4B",
  },
  {
    key: "heal",
    eyebrow: "Stage 03 · Heal",
    title: "Softening into safety",
    body: "Inside our home, medical detox, therapy and daily rhythm let the body settle. Something starts to grow again.",
    quote: "\"For the first time in years, I could rest.\"",
    stat: "28 days",
    statLabel: "Of medically supervised inpatient care, therapy & rhythm",
    img: "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&w=2000&q=75",
    overlay:
      "linear-gradient(180deg, rgba(16,28,40,0.35) 0%, rgba(16,28,40,0.15) 55%, rgba(16,28,40,0.55) 100%)",
    accent: "#CE6A4B",
  },
  {
    key: "rise",
    eyebrow: "Stage 04 · Rise",
    title: "A new sky, a new life",
    body: "You leave lighter than you arrived — with tools, a plan, a community, and the wide horizon of what comes next.",
    quote: "\"I did not know I could feel this free.\"",
    stat: "Lifetime",
    statLabel: "Aftercare & alumni community walking with you for years",
    img: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&w=2000&q=75",
    overlay:
      "linear-gradient(180deg, rgba(18,34,50,0.35) 0%, rgba(18,34,50,0.15) 55%, rgba(18,34,50,0.55) 100%)",
    accent: "#CE6A4B",
  },
];

/* One photographic stage layer with crossfade + Ken Burns + parallax */
const StageLayer = ({ stage, p, from, to }) => {
  // Crossfade window
  const opacity = useTransform(p, [from - 0.08, from, to, to + 0.08], [0, 1, 1, 0]);
  // Ken Burns — slow zoom + drift while stage is on screen
  const local = useTransform(p, [from, to], [0, 1]);
  const scale = useTransform(local, [0, 1], [1.05, 1.18]);
  const y = useTransform(local, [0, 1], ["-2%", "2%"]);
  const x = useTransform(local, [0, 1], ["-1%", "1%"]);

  return (
    <motion.div
      className="absolute inset-0 will-change-transform"
      style={{ opacity }}
      aria-hidden
    >
      <motion.div
        className="absolute inset-0"
        style={{ scale, x, y }}
      >
        <img
          src={stage.img}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
      </motion.div>
      {/* Cinematic gradient overlay */}
      <div className="absolute inset-0" style={{ background: stage.overlay }} />
      {/* Subtle blue tint to bind the section to the brand palette */}
      <div className="absolute inset-0 bg-[#1E4E6B]/10 mix-blend-multiply" />
    </motion.div>
  );
};

const RecoveryArc = () => {
  const ref = useRef(null);
  const stickyRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const p = useSpring(scrollYProgress, { stiffness: 90, damping: 22, mass: 0.4 });

  // Cursor motion → parallax depth
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.5 });
  const smy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.5 });
  const onCursor = (e) => {
    const el = stickyRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onCursorLeave = () => { mx.set(0); my.set(0); };

  // Stage windows (0..0.25 / 0.25..0.5 / 0.5..0.75 / 0.75..1)
  const windows = [
    [0.0, 0.28],
    [0.25, 0.55],
    [0.5, 0.8],
    [0.72, 1.05],
  ];

  // Scene tilt — combine scroll (base) with cursor (interactive)
  const scrollRotX = useTransform(p, [0, 0.5, 1], [8, 0, -8]);
  const scrollRotY = useTransform(p, [0, 0.5, 1], [-6, 0, 6]);
  const cursorRotX = useTransform(smy, [-0.5, 0.5], [6, -6]);
  const cursorRotY = useTransform(smx, [-0.5, 0.5], [-8, 8]);
  const rotX = useTransform([scrollRotX, cursorRotX], ([a, b]) => a + b);
  const rotY = useTransform([scrollRotY, cursorRotY], ([a, b]) => a + b);

  // Deep parallax translate for image layer driven by cursor
  const layerX = useTransform(smx, [-0.5, 0.5], [24, -24]);
  const layerY = useTransform(smy, [-0.5, 0.5], [18, -18]);

  // Warm sunbeam glow — grows through the arc + drifts with cursor
  const beamOpacity = useTransform(p, [0, 0.25, 0.6, 1], [0, 0.25, 0.6, 0.9]);
  const beamY = useTransform(p, [0, 1], ["12%", "-6%"]);
  const beamX = useTransform(smx, [-0.5, 0.5], [-40, 40]);

  // Stage index for text + dots
  const stageIdx = useTransform(p, (v) => {
    if (v < 0.25) return 0;
    if (v < 0.5) return 1;
    if (v < 0.75) return 2;
    return 3;
  });

  // Moment card 3D rotation — smoothly flips on each stage boundary
  const cardRotate = useTransform(p, [0, 0.25, 0.5, 0.75, 1], [0, 90, 180, 270, 360]);
  const cardTiltX = useTransform(smy, [-0.5, 0.5], [10, -10]);
  const cardTiltY = useTransform(smx, [-0.5, 0.5], [-10, 10]);

  return (
    <section
      ref={ref}
      data-testid="recovery-arc"
      className="relative bg-black"
      style={{ height: "400vh" }}
    >
      <div
        ref={stickyRef}
        onMouseMove={onCursor}
        onMouseLeave={onCursorLeave}
        className="sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* Photographic stages (with cursor parallax) */}
        <motion.div style={{ x: layerX, y: layerY }} className="absolute inset-0">
          {STAGES.map((s, i) => (
            <StageLayer key={s.key} stage={s} p={p} from={windows[i][0]} to={windows[i][1]} />
          ))}
        </motion.div>

        {/* Warm sunbeam / glow that intensifies through the arc */}
        <motion.div
          style={{ opacity: beamOpacity, top: beamY, x: beamX }}
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
          aria-hidden
        >
          <div className="w-[85vmin] h-[85vmin] rounded-full bg-[radial-gradient(circle,rgba(255,220,160,0.55)_0%,rgba(255,180,120,0.20)_35%,rgba(255,180,120,0)_70%)]" />
        </motion.div>

        {/* 3D perspective foreground frame — subtle depth */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ perspective: "1400px" }}
        >
          <motion.div
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
            className="relative w-full h-full"
          >
            {/* Vignette for depth on all stages */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_45%,rgba(0,0,0,0.55)_100%)]" />
          </motion.div>
        </div>

        {/* Film grain */}
        <div className="absolute inset-0 grain pointer-events-none opacity-40" />

        {/* Content overlay */}
        <div className="absolute inset-0 z-10 flex flex-col justify-between pointer-events-none">
          <div className="px-5 md:px-14 pt-24 md:pt-32">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="uppercase tracking-[0.3em] text-xs text-white/75"
              data-testid="recovery-arc-eyebrow"
            >
              An Immersive Journey · From Addiction to Recovery
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="mt-4 font-serif text-white text-4xl md:text-6xl lg:text-7xl leading-[0.98] max-w-3xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)]"
            >
              From <span className="italic text-terracotta">chains</span> to
              <br />
              open <span className="italic text-terracotta">sky</span>.
            </motion.h2>
          </div>

          <div className="px-5 md:px-14 pb-16 md:pb-24">
            <div className="relative max-w-2xl min-h-[200px]">
              {STAGES.map((s, i) => (
                <StageBlock key={s.key} stageIdx={stageIdx} idx={i} s={s} />
              ))}
            </div>
          </div>
        </div>

        {/* Floating 3D "moment card" — rotates in Y as user scrolls, tilts with cursor.
            Each face shows a quote + stat for the current stage. */}
        <div
          className="hidden md:block absolute right-6 lg:right-16 top-[46%] -translate-y-1/2 z-20 pointer-events-none"
          style={{ perspective: "1400px" }}
        >
          <motion.div
            style={{
              rotateY: cardRotate,
              rotateX: cardTiltX,
              transformStyle: "preserve-3d",
            }}
            className="relative w-[300px] lg:w-[340px] h-[240px]"
          >
            {STAGES.map((s, i) => (
              <MomentFace key={s.key} s={s} idx={i} />
            ))}
          </motion.div>
        </div>

        {/* Cursor hint chip */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-6 z-20 pointer-events-none">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/25 backdrop-blur px-3 py-1.5 text-white/80 text-[11px] uppercase tracking-[0.25em]">
            <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
            Move cursor · Scroll to travel
          </span>
        </div>

        {/* Stage dots (right rail) */}
        <div className="absolute right-5 md:right-10 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-4">
          {STAGES.map((s, i) => (
            <Dot key={s.key} stageIdx={stageIdx} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const MomentFace = ({ s, idx }) => {
  // Each face is offset in 3D at 0°, 90°, 180°, 270° so the whole card rotates 360° across the arc
  const rot = idx * 90;
  return (
    <div
      style={{
        transform: `rotateY(${rot}deg) translateZ(160px)`,
        backfaceVisibility: "hidden",
      }}
      className="absolute inset-0 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/25 p-6 lg:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
      data-testid={`moment-card-${s.key}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: s.accent }}>
          {s.eyebrow}
        </span>
        <span className="w-2 h-2 rounded-full" style={{ background: s.accent }} />
      </div>
      <p className="mt-4 font-serif text-white text-xl lg:text-2xl leading-snug">
        {s.quote}
      </p>
      <div className="mt-5 pt-4 border-t border-white/15">
        <p className="font-serif text-white text-3xl lg:text-4xl leading-none">{s.stat}</p>
        <p className="mt-1 text-white/75 text-[11px] leading-snug">{s.statLabel}</p>
      </div>
    </div>
  );
};

const StageBlock = ({ stageIdx, idx, s }) => {
  const opacity = useTransform(stageIdx, (v) => (v === idx ? 1 : 0));
  const y = useTransform(stageIdx, (v) => (v === idx ? 0 : 24));
  return (
    <motion.div
      style={{ opacity, y }}
      transition={{ duration: 0.6 }}
      className="absolute inset-0"
      data-testid={`recovery-arc-stage-${s.key}`}
    >
      <p className="text-xs uppercase tracking-[0.3em]" style={{ color: s.accent }}>
        {s.eyebrow}
      </p>
      <h3 className="mt-2 font-serif text-white text-3xl md:text-5xl leading-tight drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]">
        {s.title}
      </h3>
      <p className="mt-3 text-white/85 text-base md:text-lg max-w-xl leading-relaxed drop-shadow-[0_1px_10px_rgba(0,0,0,0.5)]">
        {s.body}
      </p>
    </motion.div>
  );
};

const Dot = ({ stageIdx, idx }) => {
  const scale = useTransform(stageIdx, (v) => (v === idx ? 1.5 : 1));
  const bg = useTransform(stageIdx, (v) => (v === idx ? "#CE6A4B" : "rgba(255,255,255,0.45)"));
  return (
    <motion.span
      style={{ scale, backgroundColor: bg }}
      className="w-2.5 h-2.5 rounded-full block"
      aria-hidden
    />
  );
};

export default RecoveryArc;
